import { useContext, useEffect, useReducer } from "react";
import {
  Institution,
  Organization,
  Term,
  defaultTerm
} from "../common/interfaces/Interface";
import Terms from "./Terms";
import translate from "@/i18n/translate";
import { sendRequest } from "../../common/http";
import StudentLearningAssesment, {
  ParticipationOverview,
  defaultParticipationOverview
} from "./StudentLearningAssesment";
import ProgramCurriculumEngagement, {
  AssessmentProgressByCourse
} from "./programCurriculumEngagement/ProgramCurriculumEngagement";
import OrganizationInsights from "./OrganizationInsights";
import EmptyStateSll from "./EmptyStateSll";
import Loader from "../common/elements/Loader";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import { onScrollChangeBreadCrumb } from "../../static/js/commons";

const getDefaultSelectedTerm = (terms: Term[]): Term | null => {
  const today = new Date().getTime();
  return terms.filter((t) => {
    return (
      new Date(t.startDate).getTime() < today &&
      new Date(t.endDate).getTime() > today
    );
  })[0];
};

interface ProgramAssessments {
  participationOverview: ParticipationOverview;
  assessmentProgressByCourse: AssessmentProgressByCourse[];
}

interface ProgramAssessmentResultSet {
  participationOverview?: ParticipationOverview;
  assessmentProgressByCourse?: AssessmentProgressByCourse[];
  noDataFound?: boolean;
}

interface OrganizationContentState {
  terms: Term[] | null;
  selectedTerm: Term;
  programAssessments: ProgramAssessments;
  showLoader: boolean;
  showMainLoader: boolean;
  noDataFound: boolean;
}

interface Action {
  type: string;
  terms: Term[];
  term: Term;
  showLoader: boolean;
  showMainLoader: boolean;
  programAssessments: ProgramAssessments;
}

const defaultProgramAssessments: ProgramAssessments = {
  participationOverview: defaultParticipationOverview,
  assessmentProgressByCourse: []
};

const defaultAction: Action = {
  type: "",
  terms: [],
  term: defaultTerm,
  programAssessments: defaultProgramAssessments,
  showLoader: false,
  showMainLoader: false
};

const reducer = (state: OrganizationContentState, action: Action) => {
  switch (action.type) {
    case "setTerms":
      const selectedTerm =
        getDefaultSelectedTerm(action.terms) || action.terms[0];
      return {
        ...state,
        terms: action.terms,
        selectedTerm: selectedTerm,
        showMainLoader: false
      };

    case "selectTerm":
      return {
        ...state,
        selectedTerm: action.term,
        showLoader: true,
        programAssessments: defaultProgramAssessments
      };
    case "setProgramAssessments":
      return {
        ...state,
        programAssessments: action.programAssessments,
        showLoader: false,
        showMainLoader: false
      };
    case "setNoDataFound":
      return {
        ...state,
        noDataFound: true,
        showLoader: false,
        showMainLoader: false
      };
    case "setLoader":
      return {
        ...state,
        showLoader: false
      };
    default:
      return state;
  }
};

const initialState: OrganizationContentState = {
  terms: null,
  selectedTerm: defaultTerm,
  programAssessments: defaultProgramAssessments,
  showLoader: true,
  showMainLoader: true,
  noDataFound: false
};
export interface OrganizationContentProps {
  platformUuid: string | null;
}

export default function OrganizationContent({
  platformUuid
}: OrganizationContentProps) {
  const { institution } = useContext<AppContextInterface>(AppContext);
  useEffect(() => {
    setTimeout(function () {
      onScrollChangeBreadCrumb();
    }, 1000);
  }, []);
  return (
    <>
      {institution?.isSllSysAdminIntegrated ? (
        <OrganizationInsightsContent platformUuid={platformUuid} />
      ) : (
        <InsightsEmptyState institution={institution} />
      )}
    </>
  );
}
interface EmptyStateProps {
  institution: Institution | null;
}
const InsightsEmptyState = ({ institution }: EmptyStateProps) => {
  return (
    <>
      <OrganizationInsights noDataFound={true} />
      <div className="body -height" id="tab-components">
        <section
          className="body wm-grid tab-body-home"
          id="section_insights"
        >
          <section className="insights insights-sll">
            <div className="institution-name -bottom flex-row -justified sse-desc flex-col-sm">
              <EmptyStateSll />
            </div>
          </section>
        </section>
      </div>
    </>
  );
};

function OrganizationInsightsContent({
  platformUuid
}: OrganizationContentProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { institution } = useContext<AppContextInterface>(AppContext);

  useEffect(() => {
    const applyResult = (terms: Term[]) => {
      dispatch({
        ...defaultAction,
        type: "setTerms",
        terms: terms
      });
    };

    sendRequest(
      {
        uri: `/sa/terms`,
        camelize: true
      },
      applyResult
    );
  }, []);

  useEffect(() => {
    if (
      state.selectedTerm &&
      state.selectedTerm.uuid !== "" &&
      platformUuid &&
      platformUuid !== ""
    ) {
      const applyResult = (
        programAssessmentResultSet: ProgramAssessmentResultSet
      ) => {
        if (
          programAssessmentResultSet.participationOverview &&
          programAssessmentResultSet.assessmentProgressByCourse
        ) {
          dispatch({
            ...defaultAction,
            type: "setProgramAssessments",
            programAssessments: {
              participationOverview:
                programAssessmentResultSet.participationOverview,
              assessmentProgressByCourse:
                programAssessmentResultSet.assessmentProgressByCourse
            }
          });
        } else {
          dispatch({
            ...defaultAction,
            type: "setNoDataFound"
          });
        }
      };
      const onError = (error: string) => {
        dispatch({ ...defaultAction, type: "setLoader", showLoader: false });
      };
      let requestUri =
        "/sll/program_assessments?" +
        "term_platform_uuid=" +
        state.selectedTerm.uuid +
        "&organization_platform_uuid=" +
        platformUuid;

      sendRequest({ uri: requestUri, camelize: true }, applyResult, onError);
    }
  }, [state.selectedTerm, platformUuid]);

  const selectTerm = (term: Term) => {
    dispatch({
      ...defaultAction,
      type: "selectTerm",
      term: term
    });
  };

  return (
    <>
      <OrganizationInsights noDataFound={state.noDataFound} />
      {state.showMainLoader && <Loader />}
      <div className="body -height" id="tab-components">
        <section
          className="body wm-grid tab-body-home"
          id="section_insights"
        >
          <section className="insights insights-sll">
            <div className="institution-name -bottom flex-row -justified sse-desc flex-col-sm">
              {!state.noDataFound && (
                <h3 className="helper-label helper-default-margin">
                  {translate("app.continuousImprovement.title")}
                </h3>
              )}

              {state.terms && state.terms.length > 0 && !state.noDataFound && (
                <Terms
                  terms={state.terms}
                  selectedTerm={state.selectedTerm}
                  selectTerm={selectTerm}
                  key={state.selectedTerm.uuid}
                />
              )}
            </div>
            {state.terms &&
              state.terms.length > 0 &&
              state.selectedTerm &&
              !state.noDataFound && (
                <div>
                  <StudentLearningAssesment
                    participationOverview={
                      state.programAssessments.participationOverview
                    }
                    showLoader={state.showLoader}
                  />
                  <ProgramCurriculumEngagement
                    assessmentProgressByCourse={
                      state.programAssessments.assessmentProgressByCourse
                    }
                    showLoader={state.showLoader}
                    termUuid={state.selectedTerm.uuid}
                    platformUuid={platformUuid}
                  />
                </div>
              )}
            {state.noDataFound && <EmptyStateSll />}
            {state.terms && state.terms.length === 0 && (
              <div className="terms-empty-state-sll">
                <div className="no-terms">
                  {translate("app.studentLearningAssessment.noTerms")}
                </div>
                <div className="no-terms-message">
                  {translate("app.studentLearningAssessment.noTermsMessage")}
                </div>
              </div>
            )}
          </section>
        </section>
      </div>
    </>
  );
}
